'use client';

import React, { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import Navbar from './Navbar';
import ImpersonationBanner from './ImpersonationBanner';
import EmailVerifyBanner from './EmailVerifyBanner';
import { useMaintenance, useAuth } from '@/app/providers';
import { useConciergeEnabled } from '@/hooks/useConciergeEnabled';

// Code-split chrome components to reduce first-load JS.
// - Footer: SSR=true (SEO-relevant, server renders HTML, JS lazy-loaded)
// - CookieConsent / PromoPortal / DemoBanner / LandingPage: SSR=false (client-only, skip SSR bundle)
const Footer = dynamic(() => import('./Footer'));
const CookieConsent = dynamic(() => import('./CookieConsent'), { ssr: false });
const PromoPortal = dynamic(() => import('@/components/promo/PromoPortal'), { ssr: false });
const DemoBanner = dynamic(() => import('./DemoBanner'), { ssr: false });
// AIChat: chatbot concierge, lazy-loaded client-only, gated by feature flag
const AIChat = dynamic(() => import('./AIChat'), { ssr: false });
const WhatsAppFloatingButton = dynamic(() => import('./WhatsAppFloatingButton'), { ssr: false });
// LandingPage NON e' dynamic/ssr:false — deve funzionare nel SSR per maintenance mode
import LandingPage from './LandingPage';

interface ClientLayoutProps {
  children: React.ReactNode;
}

// Inner component that can access maintenance context
const LayoutContent: React.FC<ClientLayoutProps> = ({ children }) => {
  const { isMaintenanceMode, maintenanceLoading, isDevAuthenticated, handleDevLogin } = useMaintenance();
  const { user, loading: authLoading, showImpersonationBanner } = useAuth();
  const { enabled: conciergeEnabled } = useConciergeEnabled();
  const pathname = usePathname();
  const router = useRouter();
  const isAdmin = pathname?.startsWith('/admin');
  const isVendor = pathname?.startsWith('/partner');
  const isBoatDetail = pathname?.startsWith('/boat/');
  const isPartnerLogin = pathname?.startsWith('/partner-login');
  const isDashboard = isAdmin || isVendor;
  const [isDemo, setIsDemo] = React.useState(false);
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsDemo(window.location.hostname.startsWith('demo.'));
    }
  }, []);

  // No-escape: se il cliente è arrivato da un QR (cookie di sessione ref_scope),
  // il catalogo è scopato su UN vendor → nav minimale, niente link per uscire.
  const [scoped, setScoped] = React.useState(false);
  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      setScoped(document.cookie.split('; ').some((c) => c.startsWith('ref_scope=') && c.slice('ref_scope='.length).length > 0));
    }
  }, [pathname]);

  // Routing PATH-based: dashboard rilevate via pathname (non più subdomini).
  // Niente Navbar/Footer pubblici su /admin e /partner.
  const isAdminHost = pathname?.startsWith('/admin') ?? false;
  const isPartner = (pathname?.startsWith('/partner')) ?? false;
  // Embed pubblico (widget iframe su siti terzi): NESSUN chrome (no navbar/footer/banner).
  const isEmbed = pathname?.startsWith('/embed') ?? false;

  // Redirect admin rimosso — l'admin accede da admin.insulacharter.it direttamente

  // Wait for maintenance status check — but skip if user is already authenticated
  if (maintenanceLoading && !user) {
    return null;
  }

  // Show landing page if in maintenance mode and dev not authenticated
  // Demo subdomain always bypasses maintenance
  if (isMaintenanceMode && !isDevAuthenticated && !isDemo) {
    return <LandingPage onDevLogin={handleDevLogin} />;
  }

  // Partner login: full-screen, no chrome
  if (isPartnerLogin) {
    return <>{children}</>;
  }

  // Embed pubblico: bare (il sito partner fornisce il proprio chrome attorno all'iframe).
  if (isEmbed) return <>{children}</>;

  // Partner subdomain: no public Navbar, no Footer, no Promo — vendor layout handles chrome
  // AccountSwitcher è integrato nell'header della sidebar vendor, niente floating qui.
  if (isPartner) {
    return (
      <div className="min-h-screen flex flex-col">
        <ImpersonationBanner />
        <main className={`flex-grow ${showImpersonationBanner ? 'pt-10' : ''}`}>{children}</main>
      </div>
    );
  }

  // Admin subdomain: no public Navbar, no Footer — admin layout handles chrome
  // AccountSwitcher è integrato nell'header della sidebar admin, niente floating qui.
  if (isAdminHost) {
    return (
      <div className="min-h-screen flex flex-col">
        <ImpersonationBanner />
        <main className={`flex-grow ${showImpersonationBanner ? 'pt-10' : ''}`}>{children}</main>
      </div>
    );
  }

  return (
    <div data-cid="client-layout" className={`min-h-screen flex flex-col ${isDashboard || isBoatDetail ? '' : 'bg-[#FCFCFD]'}`}>
      <ImpersonationBanner />
      <DemoBanner />
      <EmailVerifyBanner />
      {scoped ? (
        <div data-cid="scoped-bar" className="sticky top-0 z-40 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-center">
          <span className="text-sm font-black tracking-tight text-slate-800">Insula Charter</span>
        </div>
      ) : <Navbar />}
      <main className="flex-grow">{children}</main>
      <CookieConsent />
      {pathname === '/' && !scoped && <PromoPortal />}
      {!isDashboard && !scoped && <Footer />}
      {conciergeEnabled && !isDashboard && <AIChat />}
      {!isDashboard && !scoped && <WhatsAppFloatingButton />}
    </div>
  );
};

const ClientLayout: React.FC<ClientLayoutProps> = ({ children }) => {
  // Providers ora montati in app/layout.tsx (wrappa children + modal slot).
  return <LayoutContent>{children}</LayoutContent>;
};

export default ClientLayout;
